export default {
  name: 'MainPageTabs',
  props: {
    visitedTabs: {
      type: Array,
      default: () => []
    },
    currentTabName: {
      type: String,
      default: ''
    },
    currentTab: {
      type: Object,
      default: null
    },
    pageCacheEnabled: {
      type: Boolean,
      default: true
    },
    isFullscreen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tabsScrollTimer: null,
      tabsScrollHoldTimer: null,
      tabsScrollHoldFrame: null,
      tabsScrollHoldDirection: 0,
      tabsScrollHoldLastTime: 0,
      tabsScrollHoldTriggered: false,
      tabsOverflow: false,
      tabsCanScrollLeft: false,
      tabsCanScrollRight: false,
      tabSlider: {
        left: 0,
        width: 0,
        visible: false
      }
    }
  },
  mounted() {
    this.scrollActiveTabIntoView(false)
    window.addEventListener('resize', this.handleTabsViewportChange)
    window.addEventListener('blur', this.stopTabsScrollHold)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleTabsViewportChange)
    window.removeEventListener('blur', this.stopTabsScrollHold)
    if (this.tabsScrollTimer) window.clearTimeout(this.tabsScrollTimer)
    this.stopTabsScrollHold(true)
  },
  computed: {
    tabSliderStyle() {
      return {
        width: this.tabSlider.width + 'px',
        transform: 'translate3d(' + this.tabSlider.left + 'px, 0, 0)'
      }
    }
  },
  methods: {
    tabIconType(tag) {
      const icon = tag && tag.icon ? String(tag.icon).trim() : ''
      if (icon && /^[a-z][a-z0-9-]*$/.test(icon)) return icon
      return this.visitedTabs[0] && tag.name === this.visitedTabs[0].name ? 'appstore' : 'menu'
    },
    getTabsActionPopupContainer() {
      return document.body
    },
    scrollActiveTabIntoView(smooth = true) {
      this.$nextTick(() => {
        const tabsWrap = this.$refs.pageTabs
        this.updateTabsScrollState()
        if (!tabsWrap) {
          this.updateTabSlider()
          return
        }
        const activeTab = tabsWrap.querySelector('.page-tab.active')
        if (!activeTab) {
          this.updateTabSlider()
          return
        }
        const targetLeft = Math.max(
          0,
          Math.min(
            activeTab.offsetLeft + activeTab.offsetWidth / 2 - tabsWrap.clientWidth / 2,
            tabsWrap.scrollWidth - tabsWrap.clientWidth
          )
        )
        if (Math.abs(tabsWrap.scrollLeft - targetLeft) < 1) {
          this.updateTabSlider()
          return
        }
        if (tabsWrap.scrollTo) {
          tabsWrap.scrollTo({
            left: targetLeft,
            behavior: smooth ? 'smooth' : 'auto'
          })
        } else {
          tabsWrap.scrollLeft = targetLeft
        }
        window.setTimeout(() => {
          this.updateTabSlider()
          this.updateTabsScrollState()
        }, smooth ? 180 : 0)
      })
    },
    updateTabsScrollState() {
      const tabsWrap = this.$refs.pageTabs
      if (!tabsWrap) {
        this.tabsOverflow = false
        this.tabsCanScrollLeft = false
        this.tabsCanScrollRight = false
        return
      }

      const prevButton = this.$refs.tabsScrollPrev
      const nextButton = this.$refs.tabsScrollNext
      const controlsWidth = this.tabsOverflow
        ? (prevButton ? prevButton.offsetWidth : 0) + (nextButton ? nextButton.offsetWidth : 0)
        : 0
      const availableWidth = tabsWrap.clientWidth + controlsWidth
      const overflow = tabsWrap.scrollWidth > availableWidth + 1
      const overflowChanged = overflow !== this.tabsOverflow

      this.tabsOverflow = overflow
      if (!overflow) {
        tabsWrap.scrollLeft = 0
        this.tabsCanScrollLeft = false
        this.tabsCanScrollRight = false
      } else {
        const maxScrollLeft = Math.max(0, tabsWrap.scrollWidth - tabsWrap.clientWidth)
        this.tabsCanScrollLeft = tabsWrap.scrollLeft > 1
        this.tabsCanScrollRight = tabsWrap.scrollLeft < maxScrollLeft - 1
      }

      if (overflowChanged) {
        this.$nextTick(() => {
          this.updateTabsScrollState()
          this.scrollActiveTabIntoView(false)
        })
      }
    },
    handleTabsScroll() {
      this.updateTabsScrollState()
    },
    scrollTabsBy(direction) {
      const tabsWrap = this.$refs.pageTabs
      if (!tabsWrap || !this.tabsOverflow) return
      if (direction < 0 && !this.tabsCanScrollLeft) return
      if (direction > 0 && !this.tabsCanScrollRight) return

      const maxScrollLeft = Math.max(0, tabsWrap.scrollWidth - tabsWrap.clientWidth)
      const distance = Math.max(180, Math.min(360, tabsWrap.clientWidth * 0.55))
      const targetLeft = Math.max(0, Math.min(tabsWrap.scrollLeft + direction * distance, maxScrollLeft))
      if (tabsWrap.scrollTo) {
        tabsWrap.scrollTo({ left: targetLeft, behavior: 'smooth' })
      } else {
        tabsWrap.scrollLeft = targetLeft
      }
      window.setTimeout(this.updateTabsScrollState, 220)
    },
    handleTabsScrollButtonClick(direction) {
      if (this.tabsScrollHoldTriggered) {
        this.tabsScrollHoldTriggered = false
        return
      }
      this.scrollTabsBy(direction)
    },
    startTabsScrollHold(direction, event) {
      if (event && event.pointerType !== 'touch' && event.button !== 0) return
      if (direction < 0 && !this.tabsCanScrollLeft) return
      if (direction > 0 && !this.tabsCanScrollRight) return

      this.stopTabsScrollHold(true)
      this.tabsScrollHoldDirection = direction
      this.tabsScrollHoldTimer = window.setTimeout(() => {
        this.tabsScrollHoldTimer = null
        this.tabsScrollHoldTriggered = true
        this.tabsScrollHoldLastTime = 0
        this.tabsScrollHoldFrame = window.requestAnimationFrame(this.runTabsScrollHold)
      }, 320)
    },
    runTabsScrollHold(timestamp) {
      const tabsWrap = this.$refs.pageTabs
      const direction = this.tabsScrollHoldDirection
      if (!tabsWrap || !direction) {
        this.stopTabsScrollHold()
        return
      }

      const elapsed = this.tabsScrollHoldLastTime
        ? Math.min(32, timestamp - this.tabsScrollHoldLastTime)
        : 16
      this.tabsScrollHoldLastTime = timestamp
      const previousLeft = tabsWrap.scrollLeft
      tabsWrap.scrollLeft += direction * elapsed * 0.58

      const maxScrollLeft = Math.max(0, tabsWrap.scrollWidth - tabsWrap.clientWidth)
      const reachedEdge = Math.abs(tabsWrap.scrollLeft - previousLeft) < 0.5 ||
        (direction < 0 && tabsWrap.scrollLeft <= 1) ||
        (direction > 0 && tabsWrap.scrollLeft >= maxScrollLeft - 1)
      if (reachedEdge) {
        this.stopTabsScrollHold()
        return
      }
      this.tabsScrollHoldFrame = window.requestAnimationFrame(this.runTabsScrollHold)
    },
    stopTabsScrollHold(resetTriggered = false) {
      if (this.tabsScrollHoldTimer) {
        window.clearTimeout(this.tabsScrollHoldTimer)
        this.tabsScrollHoldTimer = null
      }
      if (this.tabsScrollHoldFrame) {
        window.cancelAnimationFrame(this.tabsScrollHoldFrame)
        this.tabsScrollHoldFrame = null
      }
      this.tabsScrollHoldDirection = 0
      this.tabsScrollHoldLastTime = 0
      if (resetTriggered === true) this.tabsScrollHoldTriggered = false
    },
    updateTabSlider() {
      this.$nextTick(() => {
        const tabsWrap = this.$refs.pageTabs
        const activeTab = tabsWrap && tabsWrap.querySelector('.page-tab.active')
        if (!activeTab) {
          this.tabSlider.visible = false
          return
        }
        const width = Math.max(22, activeTab.offsetWidth * 0.4)
        this.tabSlider = {
          left: activeTab.offsetLeft + (activeTab.offsetWidth - width) / 2,
          width,
          visible: true
        }
      })
    },
    handleTabsViewportChange() {
      if (this.tabsScrollTimer) window.clearTimeout(this.tabsScrollTimer)
      this.tabsScrollTimer = window.setTimeout(() => {
        this.scrollActiveTabIntoView(false)
        this.tabsScrollTimer = null
      }, 120)
    },
  }
}



// WEBPACK FOOTER //
// ./src/views/main/components/PageTabs/index.js